import { useEffect, useState } from "react";
import { useFormulaEvaluator } from "../../hooks";
import { useGutter } from "./useGutter";
import { useProductSelection } from "./useProductSelection";
import { useCartSync } from "./useCartSync";
import { useSupplierPricing } from "./useSupplierPricing";

export default function useMaterialsUpdate(
    measurements: any,
    materialSchema: any,
    dependencies: any,
    cartItems: any,
    setAccordionGroupStatus: any,
    setOpenAdditionalProductDialog: any,
    overrideCalculations: boolean = true
) {
    const [selectedValues, setSelectedValues] = useState<any>({});
    const [additionalProductsSelected, setAdditionalSelectedProducts] = useState<any>([]);
    const { computedValues } = useFormulaEvaluator();

    // Cart synchronization hook
    const { changeInitiated, setChangeInitiated, loadInitialCartItems, syncCartWithDatabase, isInitialLoadRef } =
        useCartSync({
            selectedValues,
            materialSchema,
            onValuesLoaded: setSelectedValues,
            additionalProductsSelected,
            onAdditionalProductsLoaded: setAdditionalSelectedProducts,
        });


    const { state: gutterState } = useGutter();
    const { handleValueChange, handleColorChange, handleQuantityUpdate } = useProductSelection({
        measurements,
        materialSchema,
        dependencies,
        selectedValues,
        setSelectedValues,
        setChangeInitiated,
        gutterLengths: {
            gutterLength: gutterState.gutterLength,
            downspoutLength: gutterState.downspoutLength,
        },
        overrideWithCalculatedValues: overrideCalculations
    });

    // Supplier pricing hook
    useSupplierPricing({
        selectedValues,
        setSelectedValues,
    });

    // Load initial cart items when cartItems change
    useEffect(() => {
        loadInitialCartItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cartItems]);

    // Sync cart with database when selectedValues or additionalProductsSelected change
    useEffect(() => {
        // Skip sync during initial load
        if (isInitialLoadRef.current) {
            return;
        }
        // Only sync if changeInitiated is true (set by handleValueChange, handleColorChange, or handleQuantityUpdate)
        // This ensures we don't sync unnecessarily and respects the flow for products with colors
        // Use a small timeout to ensure state updates have been processed
        if (changeInitiated) {
            const timeoutId = setTimeout(() => {
                syncCartWithDatabase();
            }, 0);
            return () => clearTimeout(timeoutId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedValues, additionalProductsSelected, changeInitiated]);

    // Handle accordion expand/collapse
    const handleExpandGroupedMaterialsClick = (clickedItem: string) => {
        setAccordionGroupStatus((current: any) => {
            return {
                ...current,
                [clickedItem]: {
                    ...current?.[clickedItem],
                    expanded: !current?.[clickedItem]?.expanded,
                },
            };
        });
    };

    // Handle mini inventory continue (additional products)
    const handleMiniInventoryContinueClick = (selectedProducts: any) => {
        setOpenAdditionalProductDialog(false);
        setChangeInitiated(true);

        const additionalProductsShallow = { ...additionalProductsSelected };

        // Build a set of all product names that are already in selectedValues (main selections + dependencies)
        const existingProductNames = new Set<string>();
        for (const [category, data] of Object.entries(selectedValues || {})) {
            const productData = data as any;
            if (productData?.value) {
                existingProductNames.add(productData.value);
            }
        }

        for (const [selectedProduct, productValues] of Object.entries(selectedProducts)) {
            const isPartOfPreloadedProductList = materialSchema.some(
                (product: any) => product.name === selectedProduct
            );

            if (isPartOfPreloadedProductList) {
                const selectedProductInMainPage = selectedValues?.[selectedProduct]?.value || "";

                for (const [selectedItem, selectedItemValue] of Object.entries(
                    productValues as Record<string, { quantity?: number }>
                )) {
                    // Skip items with quantity 0 or less
                    if (!selectedItemValue?.quantity || selectedItemValue.quantity <= 0) {
                        continue;
                    }

                    // If this item matches the main selection, update quantity only
                    if (selectedItem === selectedProductInMainPage) {
                        setSelectedValues((prev: any) => ({
                            ...prev,
                            [selectedProduct]: {
                                ...prev[selectedProduct],
                                quantity: selectedItemValue?.quantity ?? 0,
                            },
                        }));
                        continue;
                    }

                    // Skip if this item is already in selectedValues (as a dependency or main selection)
                    if (existingProductNames.has(selectedItem)) {
                        continue;
                    }

                    // Only add as additional product if it's not already in additionalProductsSelected
                    const identifier = `${selectedItem}_${selectedProduct}`;
                    if (!additionalProductsShallow[identifier]) {
                        additionalProductsShallow[identifier] = {
                            name: selectedItem,
                            product: selectedProduct,
                            ...selectedItemValue,
                        };
                    }
                }
            } else {
                // Handle products not in materialSchema (e.g., labor items)
                for (const [selectedItem, selectedItemValue] of Object.entries(
                    productValues as Record<string, { quantity?: number }>
                )) {
                    // Skip items with quantity 0 or less
                    if (!selectedItemValue?.quantity || selectedItemValue.quantity <= 0) {
                        continue;
                    }

                    // Skip if this item is already in selectedValues
                    if (existingProductNames.has(selectedItem)) {
                        continue;
                    }

                    // Only add as additional product if it's not already in additionalProductsSelected
                    const identifier = `${selectedItem}_${selectedProduct}`;
                    if (!additionalProductsShallow[identifier]) {
                        additionalProductsShallow[identifier] = {
                            name: selectedItem,
                            product: selectedProduct,
                            ...selectedItemValue,
                        };
                    }
                }
            }
        }

        setAdditionalSelectedProducts(additionalProductsShallow);
    };

    // Handle material removal
    const handleRemoveMaterial = (productCategory: string) => {
        setChangeInitiated(true);
        setSelectedValues((prev: any) => {
            const updated = { ...prev };
            delete updated[productCategory];
            return updated;
        });
    };

    // Handle additional product removal
    const handleRemoveAdditionalProduct = (identifier: string) => {
        setChangeInitiated(true);
        setAdditionalSelectedProducts((prev: any) => {
            const updated = { ...prev };
            delete updated[identifier];
            return updated;
        });
    };

    // Wrapper for handleQuantityUpdate to pass cartItems
    const handleQuantityUpdateWithCart = (
        productCategory: any,
        eventType: "add" | "remove",
        additionalItem = false
    ) => {
        handleQuantityUpdate(productCategory, eventType, additionalItem, cartItems);
    };

    return {
        handleValueChange,
        handleColorChange,
        handleExpandGroupedMaterialsClick,
        handleQuantityUpdate: handleQuantityUpdateWithCart,
        handleMiniInventoryContinueClick,
        handleRemoveMaterial,
        handleRemoveAdditionalProduct,
        selectedValues,
        computedValues,
        additionalProductsSelected,
    };
}
